import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus, Phone, Mail, Sparkles, Target, UserCheck, Clock, AlertTriangle,
  Edit2, Trash2, MessageSquare, CalendarClock, Search, Filter, ChevronDown,
  ChevronUp, X, Send, Eye, Users, ArrowRight, MapPin, IndianRupee, Building2
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'contacted', label: 'Contacted', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'interested', label: 'Interested', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'site_visit', label: 'Site Visit', color: 'bg-teal-100 text-teal-700 border-teal-200' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'closed', label: 'Closed Won', color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700 border-red-200' }
];

const PRIORITIES = [
  { value: 'high', label: 'High', color: 'bg-red-100 text-red-700' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-600' }
];

const SOURCES = ['Website', 'Referral', 'Walk-in', 'Phone Call', 'Social Media', 'Agent', 'Advertisement', 'Other'];

function getStatusStyle(status) {
  return STATUSES.find(s => s.value === status)?.color || 'bg-slate-100 text-slate-600';
}
function getStatusLabel(status) {
  return STATUSES.find(s => s.value === status)?.label || status;
}
function getPriorityStyle(p) {
  return PRIORITIES.find(x => x.value === p)?.color || 'bg-slate-100 text-slate-600';
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date(new Date().toDateString());
}
function isToday(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr).toDateString() === new Date().toDateString();
}
function formatDate(d) {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d; }
}

export default function Leads({ user, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamUsers, setTeamUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailLead, setDetailLead] = useState(null);
  const [followupDialog, setFollowupDialog] = useState(false);
  const [followupLead, setFollowupLead] = useState(null);
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignLeadId, setAssignLeadId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssigned, setFilterAssigned] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', property_type: '', budget: '',
    source: '', notes: '', priority: 'medium', address: ''
  });
  const [editingLead, setEditingLead] = useState(null);

  const [followupForm, setFollowupForm] = useState({
    note: '', outcome: '', next_date: '', next_note: '', status: ''
  });

  const isAdmin = user?.role === 'admin';

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/leads');
      setLeads(res.data || []);
    } catch { toast.error('Leads load nahi ho payi'); }
    finally { setLoading(false); }
  }, []);

  const fetchTeamUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/users');
      setTeamUsers((res.data || []).filter(u => u.role === 'sales' || u.role === 'admin'));
    } catch {}
  }, [isAdmin]);

  useEffect(() => { fetchLeads(); fetchTeamUsers(); }, [fetchLeads, fetchTeamUsers]);

  // Filters
  const filtered = leads.filter(l => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      if (!(l.name || '').toLowerCase().includes(q) && !(l.phone || '').includes(q) && !(l.email || '').toLowerCase().includes(q)) return false;
    }
    if (filterStatus !== 'all' && (l.status || '').toLowerCase() !== filterStatus.toLowerCase()) return false;
    if (filterAssigned === 'unassigned' && l.assigned_to) return false;
    if (filterAssigned !== 'all' && filterAssigned !== 'unassigned' && l.assigned_to !== filterAssigned) return false;
    if (filterPriority !== 'all' && l.priority !== filterPriority) return false;
    return true;
  });

  const overdueLeads = leads.filter(l => isOverdue(l.next_followup_date) && l.status !== 'closed' && l.status !== 'lost');
  const todayLeads = leads.filter(l => isToday(l.next_followup_date) && l.status !== 'closed' && l.status !== 'lost');

  const resetForm = () => setFormData({ name: '', phone: '', email: '', property_type: '', budget: '', source: '', notes: '', priority: 'medium', address: '' });

  const openAdd = () => { resetForm(); setEditingLead(null); setDialogOpen(true); };
  const openEdit = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name || '', phone: lead.phone || '', email: lead.email || '',
      property_type: lead.property_type || '', budget: lead.budget || '',
      source: lead.source || '', notes: lead.notes || '', priority: lead.priority || 'medium',
      address: lead.address || ''
    });
    setDialogOpen(true);
  };

  const handleSaveLead = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData, budget: formData.budget ? parseFloat(formData.budget) : null };
      if (editingLead) {
        await api.patch(`/leads/${editingLead.id}`, payload);
        toast.success('Lead updated!');
      } else {
        await api.post('/leads', payload);
        toast.success('Lead created!');
      }
      setDialogOpen(false);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving lead');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kya aap yeh lead delete karna chahte hain?')) return;
    try { await api.delete(`/leads/${id}`); toast.success('Lead deleted'); fetchLeads(); }
    catch { toast.error('Delete failed'); }
  };

  const handleStatusChange = async (leadId, status) => {
    try {
      await api.patch(`/leads/${leadId}`, { status });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status } : l));
      toast.success('Status updated!');
    } catch { toast.error('Update failed'); }
  };

  const handleAssign = async (leadId, userId) => {
    try {
      await api.post(`/leads/${leadId}/assign`, { user_id: userId });
      toast.success('Lead assigned!');
      setAssignDialog(false);
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Assign failed'); }
  };

  const handleBulkAssign = async (userId) => {
    if (selectedIds.size === 0) return;
    try {
      await api.post('/leads/bulk-assign', { lead_ids: Array.from(selectedIds), user_id: userId });
      toast.success(`${selectedIds.size} leads assigned!`);
      setSelectedIds(new Set());
      setAssignDialog(false);
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk assign failed'); }
  };

  const openFollowup = (lead) => {
    setFollowupLead(lead);
    setFollowupForm({ note: '', outcome: '', next_date: '', next_note: '', status: '' });
    setFollowupDialog(true);
  };

  const handleAddFollowup = async (e) => {
    e.preventDefault();
    if (!followupLead) return;
    setSaving(true);
    try {
      await api.post(`/leads/${followupLead.id}/followup`, followupForm);
      toast.success('Follow-up added!');
      setFollowupDialog(false);
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const openDetail = async (lead) => {
    try {
      const res = await api.get(`/leads/${lead.id}`);
      setDetailLead(res.data);
    } catch { setDetailLead(lead); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Stats
  const stats = {
    total: leads.length,
    new: leads.filter(l => (l.status || '').toLowerCase() === 'new').length,
    interested: leads.filter(l => ['interested', 'site_visit'].includes((l.status || '').toLowerCase())).length,
    closed: leads.filter(l => (l.status || '').toLowerCase() === 'closed').length,
    overdue: overdueLeads.length,
    today: todayLeads.length
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-4 max-w-7xl mx-auto" data-testid="leads-page">
        {/* Header Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('all')}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
              <p className="text-xs text-slate-500">Total Leads</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilterStatus('new')}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.new}</p>
              <p className="text-xs text-slate-500">New</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilterStatus('interested')}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-emerald-600">{stats.interested}</p>
              <p className="text-xs text-slate-500">Interested</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilterStatus('closed')}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.closed}</p>
              <p className="text-xs text-slate-500">Closed Won</p>
            </CardContent>
          </Card>
          {stats.overdue > 0 && (
            <Card className="border-red-200 bg-red-50 cursor-pointer hover:shadow-md" onClick={() => { setFilterStatus('all'); }}>
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
                <p className="text-xs text-red-600 font-medium">Overdue Follow-ups</p>
              </CardContent>
            </Card>
          )}
          {stats.today > 0 && (
            <Card className="border-amber-200 bg-amber-50 cursor-pointer hover:shadow-md">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.today}</p>
                <p className="text-xs text-amber-600 font-medium">Today's Follow-ups</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ========== FOLLOW-UP REMINDERS SECTION ========== */}
        {(overdueLeads.length > 0 || todayLeads.length > 0) && (
          <Card className="border-l-4 border-l-amber-400 shadow-md" data-testid="followup-reminders-section">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarClock size={18} className="text-amber-600" />
                Follow-up Reminders
                <span className="text-xs font-normal text-slate-500 ml-1">({overdueLeads.length + todayLeads.length} pending)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Overdue Section */}
              {overdueLeads.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-red-600 mb-2 flex items-center gap-1">
                    <AlertTriangle size={13} /> OVERDUE ({overdueLeads.length})
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {overdueLeads.map(lead => (
                      <div key={lead.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg group hover:shadow-sm transition-shadow" data-testid={`reminder-overdue-${lead.id}`}>
                        <div className="flex-1 min-w-0 mr-2">
                          <p className="font-semibold text-sm text-slate-800 truncate">{lead.name}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1"><Phone size={10} /> {lead.phone}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-red-600 font-medium">{formatDate(lead.next_followup_date)}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${getStatusStyle(lead.status)}`}>{getStatusLabel(lead.status)}</span>
                          </div>
                          {lead.next_followup_note && <p className="text-xs text-slate-400 mt-1 truncate">{lead.next_followup_note}</p>}
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button size="sm" onClick={() => openFollowup(lead)} className="bg-red-600 hover:bg-red-700 text-white h-7 px-2 text-xs gap-1" data-testid={`reminder-followup-${lead.id}`}>
                            <Phone size={11} /> Call
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openDetail(lead)} className="h-7 px-2 text-xs border-red-200 text-red-600">
                            <Eye size={11} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Today's Follow-ups */}
              {todayLeads.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-amber-600 mb-2 flex items-center gap-1">
                    <Clock size={13} /> TODAY ({todayLeads.length})
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {todayLeads.map(lead => (
                      <div key={lead.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg group hover:shadow-sm transition-shadow" data-testid={`reminder-today-${lead.id}`}>
                        <div className="flex-1 min-w-0 mr-2">
                          <p className="font-semibold text-sm text-slate-800 truncate">{lead.name}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1"><Phone size={10} /> {lead.phone}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-amber-600 font-medium">Today</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${getStatusStyle(lead.status)}`}>{getStatusLabel(lead.status)}</span>
                          </div>
                          {lead.next_followup_note && <p className="text-xs text-slate-400 mt-1 truncate">{lead.next_followup_note}</p>}
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button size="sm" onClick={() => openFollowup(lead)} className="bg-amber-600 hover:bg-amber-700 text-white h-7 px-2 text-xs gap-1" data-testid={`reminder-followup-today-${lead.id}`}>
                            <Phone size={11} /> Call
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openDetail(lead)} className="h-7 px-2 text-xs border-amber-200 text-amber-600">
                            <Eye size={11} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Toolbar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input placeholder="Search name, phone, email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 w-64" data-testid="lead-search" />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-36" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="w-32"><SelectValue placeholder="Priority" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    {PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {isAdmin && (
                  <Select value={filterAssigned} onValueChange={setFilterAssigned}>
                    <SelectTrigger className="w-40" data-testid="filter-assigned"><SelectValue placeholder="Assigned To" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Members</SelectItem>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {teamUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {(filterStatus !== 'all' || filterPriority !== 'all' || filterAssigned !== 'all' || searchQuery) && (
                  <Button variant="ghost" size="sm" onClick={() => { setFilterStatus('all'); setFilterPriority('all'); setFilterAssigned('all'); setSearchQuery(''); }}>
                    <X size={14} className="mr-1" /> Clear
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && selectedIds.size > 0 && (
                  <Button variant="outline" onClick={() => { setAssignLeadId(null); setAssignDialog(true); }} className="gap-1 text-blue-600" data-testid="bulk-assign-btn">
                    <UserCheck size={14} /> Assign {selectedIds.size}
                  </Button>
                )}
                <Button onClick={openAdd} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="new-lead-button">
                  <Plus size={16} /> New Lead
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leads Table */}
        <Card>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead className="w-10"><input type="checkbox" checked={filtered.length > 0 && selectedIds.size === filtered.length} onChange={() => { if (selectedIds.size === filtered.length) setSelectedIds(new Set()); else setSelectedIds(new Set(filtered.map(l => l.id))); }} className="w-4 h-4 accent-emerald-600" /></TableHead>}
                  <TableHead>Lead</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Property / Budget</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  {isAdmin && <TableHead>Assigned To</TableHead>}
                  <TableHead>Next Follow-up</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={isAdmin ? 9 : 7} className="text-center py-8 text-slate-500">
                    <div className="flex items-center justify-center gap-2"><div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>Loading...</div>
                  </TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={isAdmin ? 9 : 7} className="text-center py-8 text-slate-500">Koi lead nahi mili</TableCell></TableRow>
                ) : filtered.map(lead => {
                  const overdue = isOverdue(lead.next_followup_date) && lead.status !== 'closed' && lead.status !== 'lost';
                  const today = isToday(lead.next_followup_date);
                  return (
                    <TableRow key={lead.id} className={`hover:bg-slate-50 ${overdue ? 'bg-red-50/50' : today ? 'bg-amber-50/50' : ''} ${selectedIds.has(lead.id) ? 'bg-emerald-50' : ''}`} data-testid={`lead-row-${lead.id}`}>
                      {isAdmin && <TableCell><input type="checkbox" checked={selectedIds.has(lead.id)} onChange={() => toggleSelect(lead.id)} className="w-4 h-4 accent-emerald-600" /></TableCell>}
                      <TableCell>
                        <div>
                          <p className="font-semibold text-slate-800">{lead.name}</p>
                          <p className="text-xs text-slate-400">{lead.source && `via ${lead.source}`} {lead.ai_score ? `| AI: ${Math.round(lead.ai_score)}` : ''}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5 text-sm">
                          <div className="flex items-center gap-1 text-slate-600"><Phone size={12} /> {lead.phone}</div>
                          {lead.email && <div className="flex items-center gap-1 text-slate-500 text-xs"><Mail size={11} /> {lead.email}</div>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {lead.property_type && <span className="text-slate-700">{lead.property_type}</span>}
                          {lead.budget > 0 && <p className="text-xs text-emerald-600 font-medium">Rs. {lead.budget.toLocaleString('en-IN')}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <select value={lead.status} onChange={e => handleStatusChange(lead.id, e.target.value)} className={`text-xs font-semibold px-2 py-1 rounded-full border cursor-pointer ${getStatusStyle(lead.status)}`} data-testid={`status-select-${lead.id}`}>
                          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getPriorityStyle(lead.priority)}`}>{(lead.priority || 'medium').charAt(0).toUpperCase() + (lead.priority || 'medium').slice(1)}</span>
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          {lead.assigned_to_name ? (
                            <span className="text-sm text-blue-700 font-medium">{lead.assigned_to_name}</span>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => { setAssignLeadId(lead.id); setAssignDialog(true); }} className="text-xs text-slate-400 hover:text-blue-600" data-testid={`assign-btn-${lead.id}`}>
                              <UserCheck size={12} className="mr-1" /> Assign
                            </Button>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        {lead.next_followup_date ? (
                          <div className={`text-xs ${overdue ? 'text-red-600 font-bold' : today ? 'text-amber-600 font-semibold' : 'text-slate-600'}`}>
                            {overdue && <AlertTriangle size={12} className="inline mr-1" />}
                            {today && <Clock size={12} className="inline mr-1" />}
                            {formatDate(lead.next_followup_date)}
                            {lead.next_followup_note && <p className="text-slate-400 truncate max-w-[120px]">{lead.next_followup_note}</p>}
                          </div>
                        ) : <span className="text-xs text-slate-400">-</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => openDetail(lead)} className="text-slate-600 gap-1" data-testid={`view-lead-${lead.id}`}><Eye size={13} /></Button>
                          <Button size="sm" variant="outline" onClick={() => openFollowup(lead)} className="text-emerald-600 gap-1" data-testid={`followup-btn-${lead.id}`}><MessageSquare size={13} /></Button>
                          <Button size="sm" variant="outline" onClick={() => openEdit(lead)} className="text-blue-600"><Edit2 size={13} /></Button>
                          {isAdmin && <Button size="sm" variant="outline" onClick={() => handleDelete(lead.id)} className="text-red-600"><Trash2 size={13} /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <p className="text-xs text-slate-400 mt-2 px-1">{filtered.length} of {leads.length} leads</p>
          </CardContent>
        </Card>

        {/* ===== ADD/EDIT LEAD DIALOG ===== */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingLead ? 'Edit Lead' : 'New Lead'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSaveLead} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Name *</Label><Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required data-testid="lead-name-input" /></div>
                <div className="space-y-1"><Label>Phone *</Label><Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} required data-testid="lead-phone-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Email</Label><Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} data-testid="lead-email-input" /></div>
                <div className="space-y-1">
                  <Label>Source</Label>
                  <Select value={formData.source} onValueChange={v => setFormData({ ...formData, source: v })}>
                    <SelectTrigger data-testid="lead-source-input"><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1"><Label>Property Type</Label><Input value={formData.property_type} onChange={e => setFormData({ ...formData, property_type: e.target.value })} placeholder="Plot, Flat, Villa" data-testid="lead-property-input" /></div>
                <div className="space-y-1"><Label>Budget</Label><Input type="number" value={formData.budget} onChange={e => setFormData({ ...formData, budget: e.target.value })} placeholder="Rs." data-testid="lead-budget-input" /></div>
                <div className="space-y-1">
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={v => setFormData({ ...formData, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1"><Label>Address</Label><Input value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} /></div>
              <div className="space-y-1"><Label>Notes</Label><textarea rows={2} value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="lead-notes-input" /></div>
              <Button type="submit" disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="submit-lead-button">{saving ? 'Saving...' : editingLead ? 'Update Lead' : 'Create Lead'}</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* ===== FOLLOW-UP DIALOG ===== */}
        <Dialog open={followupDialog} onOpenChange={setFollowupDialog}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><MessageSquare size={18} className="text-emerald-600" /> Add Follow-up</DialogTitle>
              {followupLead && <p className="text-sm text-slate-500">{followupLead.name} - {followupLead.phone}</p>}
            </DialogHeader>
            <form onSubmit={handleAddFollowup} className="space-y-3">
              <div className="space-y-1">
                <Label>Follow-up Notes *</Label>
                <textarea rows={3} value={followupForm.note} onChange={e => setFollowupForm({ ...followupForm, note: e.target.value })} required className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Kya baat hui? Client kya bola?" data-testid="followup-note" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Outcome</Label>
                  <Select value={followupForm.outcome} onValueChange={v => setFollowupForm({ ...followupForm, outcome: v })}>
                    <SelectTrigger><SelectValue placeholder="Select outcome" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call_done">Call Done</SelectItem>
                      <SelectItem value="no_answer">No Answer</SelectItem>
                      <SelectItem value="busy">Busy</SelectItem>
                      <SelectItem value="callback">Callback Requested</SelectItem>
                      <SelectItem value="interested">Interested</SelectItem>
                      <SelectItem value="not_interested">Not Interested</SelectItem>
                      <SelectItem value="site_visit_scheduled">Site Visit Scheduled</SelectItem>
                      <SelectItem value="deal_closed">Deal Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Update Status</Label>
                  <Select value={followupForm.status} onValueChange={v => setFollowupForm({ ...followupForm, status: v === 'no_change' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="No change" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no_change">No Change</SelectItem>
                      {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border-t pt-3 space-y-2">
                <Label className="flex items-center gap-1 text-emerald-700"><CalendarClock size={14} /> Schedule Next Follow-up</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Date</Label>
                    <Input type="date" value={followupForm.next_date} onChange={e => setFollowupForm({ ...followupForm, next_date: e.target.value })} data-testid="followup-next-date" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Note for next call</Label>
                    <Input value={followupForm.next_note} onChange={e => setFollowupForm({ ...followupForm, next_note: e.target.value })} placeholder="Kya discuss karna hai" />
                  </div>
                </div>
              </div>
              <Button type="submit" disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-followup-btn">{saving ? 'Saving...' : 'Add Follow-up'}</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* ===== ASSIGN DIALOG ===== */}
        <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCheck size={18} /> Assign Lead</DialogTitle></DialogHeader>
            <div className="space-y-2">
              {teamUsers.map(u => (
                <Button key={u.id} variant="outline" className="w-full justify-start gap-2" onClick={() => {
                  if (assignLeadId) handleAssign(assignLeadId, u.id);
                  else handleBulkAssign(u.id);
                }} data-testid={`assign-to-${u.id}`}>
                  <Users size={14} /> {u.full_name} <span className="text-xs text-slate-400 ml-auto">{u.role}</span>
                </Button>
              ))}
              {assignLeadId && (
                <Button variant="ghost" className="w-full text-red-500" onClick={() => handleAssign(assignLeadId, '')}>
                  <X size={14} className="mr-1" /> Unassign
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== LEAD DETAIL DIALOG ===== */}
        <Dialog open={!!detailLead} onOpenChange={(o) => { if (!o) setDetailLead(null); }}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            {detailLead && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Target size={18} className="text-emerald-600" />
                    {detailLead.name}
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ml-2 ${getStatusStyle(detailLead.status)}`}>{getStatusLabel(detailLead.status)}</span>
                  </DialogTitle>
                </DialogHeader>
                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-3 text-sm p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400" /> <span className="font-medium">{detailLead.phone}</span></div>
                  {detailLead.email && <div className="flex items-center gap-2"><Mail size={14} className="text-slate-400" /> {detailLead.email}</div>}
                  {detailLead.property_type && <div className="flex items-center gap-2"><Building2 size={14} className="text-slate-400" /> {detailLead.property_type}</div>}
                  {detailLead.budget > 0 && <div className="flex items-center gap-2"><IndianRupee size={14} className="text-slate-400" /> Rs. {detailLead.budget?.toLocaleString('en-IN')}</div>}
                  {detailLead.source && <div className="flex items-center gap-2"><ArrowRight size={14} className="text-slate-400" /> {detailLead.source}</div>}
                  {detailLead.address && <div className="flex items-center gap-2"><MapPin size={14} className="text-slate-400" /> {detailLead.address}</div>}
                  {detailLead.assigned_to_name && <div className="flex items-center gap-2"><UserCheck size={14} className="text-blue-500" /> <span className="text-blue-700">{detailLead.assigned_to_name}</span></div>}
                  <div className="flex items-center gap-2"><CalendarClock size={14} className="text-slate-400" /> Created: {formatDate(detailLead.created_at)}</div>
                </div>
                {detailLead.notes && <div className="p-3 bg-amber-50 rounded-lg text-sm border border-amber-100"><strong>Notes:</strong> {detailLead.notes}</div>}
                {/* Next Follow-up */}
                {detailLead.next_followup_date && (
                  <div className={`p-3 rounded-lg text-sm border ${isOverdue(detailLead.next_followup_date) ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                    <p className="font-semibold flex items-center gap-1">
                      {isOverdue(detailLead.next_followup_date) ? <AlertTriangle size={14} className="text-red-600" /> : <CalendarClock size={14} className="text-emerald-600" />}
                      Next Follow-up: {formatDate(detailLead.next_followup_date)}
                    </p>
                    {detailLead.next_followup_note && <p className="text-slate-600 mt-1">{detailLead.next_followup_note}</p>}
                  </div>
                )}
                {/* Follow-up History */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm flex items-center gap-1"><MessageSquare size={14} /> Follow-up History ({(detailLead.follow_ups || []).length})</h3>
                    <Button size="sm" onClick={() => { openFollowup(detailLead); setDetailLead(null); }} className="bg-emerald-600 hover:bg-emerald-700 gap-1 text-xs"><Plus size={12} /> Add Follow-up</Button>
                  </div>
                  {(detailLead.follow_ups || []).length === 0 ? (
                    <p className="text-sm text-slate-400 py-4 text-center">Koi follow-up nahi hai abhi tak</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {(detailLead.follow_ups || []).map((fu, i) => (
                        <div key={fu.id || i} className="p-3 bg-white border border-slate-100 rounded-lg text-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-slate-700">{fu.created_by}</span>
                            <span className="text-xs text-slate-400">{formatDate(fu.created_at)}</span>
                          </div>
                          <p className="text-slate-600">{fu.note}</p>
                          {fu.outcome && <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{fu.outcome.replace(/_/g, ' ')}</span>}
                          {fu.next_date && <p className="text-xs text-emerald-600 mt-1">Next: {formatDate(fu.next_date)} {fu.next_note && `- ${fu.next_note}`}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
